import {Item, ItemType, Workspace} from '../../../../API';
import {TENANT_ID, WORKSPACE_ID, CLIENT_USER_ID} from '../fixtures';

// ── Stable IDs ──────────────────────────────────────────────────────────────
const LIBRARY_ID    = 'demo-lib1-0000-0000-0000-000000000030';

const SEC_STYLIST   = 'demo-sec1-0001-0000-0000-000000000031';
const SEC_STANDARDS = 'demo-sec2-0002-0000-0000-000000000032';
const SEC_SAFETY    = 'demo-sec3-0003-0000-0000-000000000033';

const LEAF_RENTAL   = 'demo-leaf-0001-0000-0000-000000000031';
const LEAF_ONBOARD  = 'demo-leaf-0002-0000-0000-000000000032';
const LEAF_COLOR    = 'demo-leaf-0003-0000-0000-000000000033';
const LEAF_OPENING  = 'demo-leaf-0004-0000-0000-000000000034';
const LEAF_SANIT    = 'demo-leaf-0005-0000-0000-000000000035';
const LEAF_CONDUCT  = 'demo-leaf-0006-0000-0000-000000000036';

const NOW = new Date('2026-01-01T00:00:00.000Z').toISOString();

function leaf(id: string, parentId: string, name: string): Item {
    return {
        __typename: 'Item',
        id,
        ownerId: TENANT_ID,
        workspaceId: WORKSPACE_ID,
        name,
        type: ItemType.LEAF,
        childIds: '[]',
        parentIds: JSON.stringify([parentId]),
        readRoles: '[]',
        writeRoles: '[]',
        creator: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
        archived: false,
        trashed: false,
        pinned: false,
        pinnedIds: '[]',
        createdAt: NOW,
        updatedAt: NOW,
        version: 1,
        deletedAt: null,
        slug: null,
        description: null,
        acknowledgement: null,
        acknowledgementRequired: null,
        ackUpdatedBy: null,
        statusRestoreParentIds: null,
        statusSourceLibraryId: null,
        statusUpdatedBy: null,
    };
}

function library(id: string, name: string, childIds: string[], slug: string): Item {
    return {
        __typename: 'Item',
        id,
        ownerId: TENANT_ID,
        workspaceId: WORKSPACE_ID,
        name,
        type: ItemType.LIBRARY,
        childIds: JSON.stringify(childIds),
        parentIds: JSON.stringify([WORKSPACE_ID]),
        readRoles: '[]',
        writeRoles: '[]',
        creator: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
        archived: false,
        trashed: false,
        pinned: false,
        pinnedIds: '[]',
        createdAt: NOW,
        updatedAt: NOW,
        version: 1,
        deletedAt: null,
        slug,
        description: null,
        acknowledgement: null,
        acknowledgementRequired: null,
        ackUpdatedBy: null,
        statusRestoreParentIds: null,
        statusSourceLibraryId: null,
        statusUpdatedBy: null,
    };
}

function cluster(id: string, name: string, childIds: string[]): Item {
    return {
        __typename: 'Item',
        id,
        ownerId: TENANT_ID,
        workspaceId: WORKSPACE_ID,
        name,
        type: ItemType.CLUSTER,
        childIds: JSON.stringify(childIds),
        parentIds: JSON.stringify([LIBRARY_ID]),
        readRoles: '[]',
        writeRoles: '[]',
        creator: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
        archived: false,
        trashed: false,
        pinned: false,
        pinnedIds: '[]',
        createdAt: NOW,
        updatedAt: NOW,
        version: 1,
        deletedAt: null,
        slug: null,
        description: null,
        acknowledgement: null,
        acknowledgementRequired: null,
        ackUpdatedBy: null,
        statusRestoreParentIds: null,
        statusSourceLibraryId: null,
        statusUpdatedBy: null,
    };
}

// ── Items ────────────────────────────────────────────────────────────────────
export const items: Item[] = [
    library(LIBRARY_ID, 'Main Library', [SEC_STYLIST, SEC_STANDARDS, SEC_SAFETY], 'main'),

    cluster(SEC_STYLIST,   'Stylist Operations', [LEAF_RENTAL, LEAF_ONBOARD, LEAF_COLOR]),
    cluster(SEC_STANDARDS, 'Salon Standards',    [LEAF_OPENING, LEAF_CONDUCT]),
    cluster(SEC_SAFETY,    'Health & Sanitation',[LEAF_SANIT]),
    leaf(LEAF_RENTAL,  SEC_STYLIST,   'Station Rental Agreement'),
    leaf(LEAF_ONBOARD, SEC_STYLIST,   'New Stylist Onboarding'),
    leaf(LEAF_COLOR,   SEC_STYLIST,   'Color Service Protocol'),
    leaf(LEAF_OPENING, SEC_STANDARDS, 'Opening & Closing Checklist'),
    leaf(LEAF_CONDUCT, SEC_STANDARDS, 'Professional Conduct Policy'),
    leaf(LEAF_SANIT,   SEC_SAFETY,    'Sanitation & Tool Sterilization'),
];

// ── Workspace override ────────────────────────────────────────────────────────
export const workspaceOverride: Partial<Workspace> = {
    childIds: JSON.stringify([LIBRARY_ID]),
    name: 'GlossBar Studios',
};

// ── ProseMirror document content keyed by itemId ─────────────────────────────
export const docContents: Record<string, any> = {
    [LEAF_RENTAL]: {
        type: 'doc',
        content: [
            {type: 'heading', attrs: {level: 1}, content: [{type: 'text', text: 'Station Rental Agreement'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'This document outlines the terms for renting a styling station at GlossBar Studios. All stylists must read and acknowledge this page before their first day.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Rental terms'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Station rental is billed weekly, due every Monday before 10:00 AM. Late payment incurs a $25 fee per day.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Each stylist is assigned one dedicated station. Subletting or swapping stations without manager approval is not permitted.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Minimum rental period is 4 weeks. Vacating before that period forfeits the security deposit.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Station care'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Stylists are responsible for cleaning and sanitizing their station after each client. See Sanitation & Tool Sterilization for the correct procedure.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Report any equipment damage to management within 24 hours. Unreported damage found during inspection may be charged to the renting stylist.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Personal items must be stored in the assigned locker. Station surfaces must be clear of personal belongings when not in use.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Included in your rental'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Mirror, styling chair, and backwash unit (shared with the station on your left).'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Wi-Fi access and access to the shared reception booking system.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Use of the laundry service for salon towels and capes — place used items in the designated basket by 6:00 PM.'}]}]},
            ]},
        ],
    },
    [LEAF_ONBOARD]: {
        type: 'doc',
        content: [
            {type: 'heading', attrs: {level: 1}, content: [{type: 'text', text: 'New Stylist Onboarding'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'Welcome to GlossBar Studios. This guide walks you through everything you need to know before your first client.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Before your first day'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Sign and return your Station Rental Agreement and Insurance Certificate to reception.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Complete the GlossBar induction with the salon manager — allow 45 minutes.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Provide proof of current professional liability insurance (minimum $1M coverage).'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Setting up your station'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Your station number is on your Welcome Card. Locate it and check for any existing damage — report it to management before you unpack.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Your locker is labeled with your station number. The combination is in your Welcome Card — change it on your first day.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Add yourself to the shared booking system. Reception will send you login details 48 hours before your start date.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Who to contact'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Day-to-day: your floor manager (name posted at reception).'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Billing and rental: accounts@glossbarstudios.com or Ext. 201.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Maintenance issues: log via the tablet at reception — response within 4 hours during business hours.'}]}]},
            ]},
        ],
    },
    [LEAF_COLOR]: {
        type: 'doc',
        content: [
            {type: 'heading', attrs: {level: 1}, content: [{type: 'text', text: 'Color Service Protocol'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'Follow this protocol for every color service to protect clients, keep the space safe, and maintain consistent results across GlossBar Studios.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Patch test policy'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'A patch test is mandatory for every new client and for any client who has not had color in the past 6 months.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Perform the test at least 48 hours before the appointment. Record the result in the client\'s file.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'If the client refuses a patch test, do not proceed. Note the refusal in their file and inform management.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Color mixing area'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'All color mixing is done at the designated mixing stations (marked in teal). Do not mix color at your styling station.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Wear gloves and an apron before handling any color products.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Dispose of unused color mix immediately after the service — do not store mixed color.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Clean the mixing station immediately after use. Leave no product residue on surfaces.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'During the service'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Drape a color-resistant cape before application. GlossBar capes are in the laundry trolley — do not use your own.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Set a timer for processing time. Do not leave a client unattended with color applied.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'If a client shows any sign of irritation (redness, itching, burning), rinse immediately and call management.'}]}]},
            ]},
        ],
    },
    [LEAF_OPENING]: {
        type: 'doc',
        content: [
            {type: 'heading', attrs: {level: 1}, content: [{type: 'text', text: 'Opening & Closing Checklist'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'The opening and closing duties rotate weekly between floor managers. Both lists must be initialled in the logbook on the reception desk.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Opening (start 30 minutes before first appointment)'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Disable the alarm and sign the security log.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Walk the floor and check each station for cleanliness. Flag any station that was not cleaned properly the night before — the responsible stylist will be notified.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Check that all autoclave cycles from yesterday were completed. Print and file the autoclave log.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Switch on reception displays and log in to the booking system.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Fill the towel and cape trolleys. Start a laundry load if the hamper is more than half full.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Closing (last 30 minutes of trading)'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Verify all stylists have completed their station sanitation and signed off in the logbook.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Run the final autoclave cycle for the day. Print the log and attach it to today\'s file.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Empty all color-mixing bins and wipe mixing stations with disinfectant spray.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Lock the product storeroom and the cash drawer.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Set the alarm and record the closing time in the security log.'}]}]},
            ]},
        ],
    },
    [LEAF_CONDUCT]: {
        type: 'doc',
        content: [
            {type: 'heading', attrs: {level: 1}, content: [{type: 'text', text: 'Professional Conduct Policy'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'GlossBar Studios is a shared professional environment. This policy applies to all stylists renting space in any GlossBar location.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Shared space etiquette'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Music: use headphones or keep personal speakers at a volume that cannot be heard from the adjacent station.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Conversations with clients should not disturb other clients. Lower your voice if a client signals discomfort.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Do not use another stylist\'s tools, products, or equipment without their explicit permission.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Client interactions'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Greet every client by name. Confirm their booking details and patch test results before beginning.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Do not discuss pricing disputes, complaints about other stylists, or management decisions in front of clients. Direct these to management.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'If a client complaint arises that you cannot resolve, involve the floor manager immediately.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Non-compliance'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'A verbal warning is issued on the first breach. A written warning on the second. A third breach may result in termination of your rental agreement. Serious breaches (aggressive behaviour, theft, unsafe chemical use) result in immediate suspension pending investigation.'}]},
        ],
    },
    [LEAF_SANIT]: {
        type: 'doc',
        content: [
            {type: 'heading', attrs: {level: 1}, content: [{type: 'text', text: 'Sanitation & Tool Sterilization'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'This is non-negotiable. Every stylist at GlossBar is individually responsible for the hygiene of their station and tools. Health department inspections are unannounced.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'After every client'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Wipe the chair, armrests, and headrest with hospital-grade disinfectant. Allow 30-second contact time before the next client sits.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Place all used towels and capes directly in the laundry basket — do not drape on chair or counter.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Clean scissors and combs: rinse under running water, then immerse in barbicide for a minimum of 10 minutes. Remove and air-dry — do not towel-dry.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Clip any blades and metal tools that pierced skin (threading, etc.) using the autoclave. Place in the autoclave tray — do not use again until the cycle completes.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'End of day'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Clean the mirror with glass cleaner. Wipe the counter surface with disinfectant and leave it clear.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Sign the sanitation logbook at reception. Your station will be spot-checked before the logbook is closed.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Empty your barbicide jar, rinse it, and refill with fresh solution. Dilution ratio: 1 oz barbicide to 32 oz water.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Product storage'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Store all chemical products (bleach, developer, color) in your locker when not in use. Do not leave on the counter between clients.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Check MSDS sheets for any new product before first use. Copies are in the folder at the mixing station.'}]}]},
            ]},
        ],
    },
};
